import { useState, type FormEvent } from 'react';
import { sessionStore, type SessionStore } from '../state';

export interface SignInPanelProps {
  /** Why authentication is required (e.g. "Listing releases requires an operator token."). */
  reason: string;
  /** Injectable for tests; defaults to the app-wide {@link sessionStore} singleton. */
  store?: SessionStore;
}

/**
 * Operator-token sign-in gate (spec §3: the token is session-only, held in
 * memory and sessionStorage — never persisted beyond the tab session).
 * Rendered by pages whose API calls require a bearer token whenever the
 * session has none (initial visit or after a server 401 dropped it).
 */
export default function SignInPanel({ reason, store = sessionStore }: SignInPanelProps) {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      store.setToken(token);
      setToken('');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="sign-in-panel" aria-label="Operator sign-in">
      <h2>Operator sign-in required</h2>
      <p>{reason}</p>
      <form onSubmit={onSubmit} className="sign-in-form">
        <label htmlFor="operator-token">Operator token</label>
        <input
          id="operator-token"
          name="operator-token"
          type="password"
          autoComplete="off"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          placeholder="Paste bearer token"
          required
        />
        <button type="submit">Sign in</button>
      </form>
      {error !== null && (
        <p className="sign-in-error" role="alert">
          {error}
        </p>
      )}
      <p className="sign-in-note">
        The token is kept in memory and sessionStorage only; closing the tab signs you out.
      </p>
    </section>
  );
}
